import * as React from 'react'
import { Loader2, Play, Video } from 'lucide-react'
import type { TreeVideoDto } from '@lib/dto/tree-video.dto'
import { MAX_VIDEO_SECONDS } from '@lib/constants/documents'
import {
  useUploadVideo,
  useVideo,
  useVideoAvailability,
} from '@/hooks/useVideos'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { errorMessage } from '@/helpers/logger'
import { uploadingLabel } from '@/helpers/uploadLabel'

/** Add, Replace, or the progress, without a nested condition in the button. */
function videoButtonLabel(
  isUploading: boolean,
  percent: number | null,
  videoId: string | null,
): string {
  if (isUploading) return uploadingLabel(percent)
  if (videoId === null) return 'Add video'
  return 'Replace'
}

/**
 * The tile: a still to click, or a caption saying why there is not one yet.
 *
 * Stream transcodes after the upload finishes, so there is a real window where
 * the clip exists and cannot be played. Saying "Processing" is better than a
 * broken thumbnail, and the query polls until it resolves itself.
 */
function VideoTile({
  video,
  onPlay,
}: {
  video: TreeVideoDto
  onPlay: () => void
}) {
  if (video.status === 'ERRORED') {
    return (
      <div className="flex h-16 w-28 shrink-0 items-center justify-center rounded-md border border-danger bg-subtle px-2 text-center text-[0.6875rem] leading-tight text-danger">
        {video.errorMessage ?? 'Could not be processed'}
      </div>
    )
  }

  if (video.status !== 'READY') {
    return (
      <div className="flex h-16 w-28 shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-default bg-subtle text-[0.6875rem] text-muted-text">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Processing
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label="Play the clip"
      className="group relative h-16 w-28 shrink-0 overflow-hidden rounded-md border border-default bg-subtle"
    >
      {video.thumbnailUrl === null ? null : (
        <img
          src={video.thumbnailUrl}
          alt=""
          className="h-full w-full object-cover"
        />
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-map-ink/30 transition-colors group-hover:bg-map-ink/45">
        <Play className="h-5 w-5 text-map-halo" />
      </span>
    </button>
  )
}

/**
 * A clip explaining what a kind of problem looks like.
 *
 * Distinct from the stock photo in purpose: the photo stands in for a picture
 * nobody took of the actual tree, while this describes the PROBLEM — what
 * mistletoe looks like in a winter canopy, where to look for it. It is
 * reference material for whoever is standing under a tree deciding whether
 * this is that.
 *
 * Uploaded unattached and pointed at by id, exactly like the stock photo: the
 * clip has to exist before the directory entry does, because somebody adding
 * "Mistletoe" records the explanation in the same breath as the name.
 */
export function ProblemTypeVideo({
  videoId,
  onChange,
}: {
  videoId: string | null
  /** Called with the new id, or null when the clip is removed. */
  onChange: (videoId: string | null) => void
}) {
  const availability = useVideoAvailability()
  const video = useVideo(videoId)
  const [percent, setPercent] = React.useState<number | null>(null)
  const upload = useUploadVideo(null, setPercent)
  const [error, setError] = React.useState<string | null>(null)
  const [playing, setPlaying] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement | null>(null)

  /*
   * Hidden only once the check says Stream is off. Pending and a failed check
   * used to take that same path, so a slow or broken check looked like video
   * was not offered.
   */
  if (availability.isPending) {
    return (
      <div className="space-y-1.5">
        <Label>Explainer video</Label>
        <p className="flex items-center gap-1.5 text-xs text-muted-text">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Checking whether a video can be added…
        </p>
      </div>
    )
  }
  if (availability.isError) {
    return (
      <div className="space-y-1.5">
        <Label>Explainer video</Label>
        <p className="text-xs text-danger">
          {errorMessage(availability.error, 'Video could not be checked.')}
        </p>
      </div>
    )
  }
  if (availability.data === false) {
    return null
  }

  /*
   * Nothing here deletes anything, deliberately.
   *
   * This control edits an UNSAVED draft: the surrounding form holds its state
   * until Save. Deleting the replaced clip from Stream at this point was wrong
   * in a way that only appeared under Cancel — the clip was gone while the
   * stored `videoId` still pointed at it, leaving a type whose explainer 404s
   * for good, the tile spinning and the list still claiming a video was there.
   *
   * The API now releases the replaced clip when the write actually lands, so
   * the two can no longer disagree. All that is left here is discarding a clip
   * this form uploaded and never saved, which the FORM does because only it
   * knows what was there before the draft started.
   */
  async function handleFile(file: File | undefined): Promise<void> {
    if (file === undefined) return
    setError(null)
    try {
      const created = await upload.mutateAsync(file)
      onChange(created.id)
    } catch (caught) {
      setError(errorMessage(caught, 'The video could not be saved.'))
    } finally {
      setPercent(null)
      if (inputRef.current !== null) inputRef.current.value = ''
    }
  }

  function handleRemove(): void {
    setError(null)
    onChange(null)
  }

  /**
   * The tile, or why there is not one.
   *
   * `isError` is its own branch rather than being folded into "still loading".
   * A clip that has been deleted answers 404, and rendering that as a
   * permanent spinner is how a broken entry passes for a slow one.
   */
  function renderTile(): React.ReactNode {
    if (videoId === null) {
      return (
        <div className="flex h-16 w-28 shrink-0 items-center justify-center rounded-md border border-dashed border-default bg-subtle">
          <Video className="h-4 w-4 text-muted-text" />
        </div>
      )
    }
    if (video.isError) {
      return (
        <div className="flex h-16 w-28 shrink-0 items-center justify-center rounded-md border border-danger bg-subtle px-2 text-center text-[0.6875rem] leading-tight text-danger">
          This clip is no longer there
        </div>
      )
    }
    if (video.data === undefined) {
      return (
        <div className="flex h-16 w-28 shrink-0 items-center justify-center rounded-md border border-dashed border-default bg-subtle">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-text" />
        </div>
      )
    }
    return (
      <VideoTile
        video={video.data}
        onPlay={() => {
          setPlaying(true)
        }}
      />
    )
  }

  return (
    <div className="space-y-1.5">
      <Label>Explainer video</Label>
      <div className="flex items-center gap-3">
        {renderTile()}

        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(event) => {
            void handleFile(event.target.files?.[0])
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={upload.isPending}
          onClick={() => {
            inputRef.current?.click()
          }}
        >
          {upload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Video className="h-3.5 w-3.5" />
          )}
          {videoButtonLabel(upload.isPending, percent, videoId)}
        </Button>
        {videoId === null ? null : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={upload.isPending}
            onClick={handleRemove}
          >
            Remove
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-text">
        Shown in the survey when this problem is picked, so whoever is recording
        it can check what they are looking at. Up to{' '}
        {String(Math.floor(MAX_VIDEO_SECONDS / 60))} minutes.
      </p>
      {error === null ? null : <p className="text-xs text-danger">{error}</p>}

      <Dialog
        open={playing}
        onOpenChange={(open) => {
          setPlaying(open)
        }}
      >
        <DialogContent className="max-w-2xl p-0">
          <DialogTitle className="sr-only">Explainer video</DialogTitle>
          {video.data?.playbackUrl == null ? null : (
            // Stream's own iframe player, for the reasons in `SurveyVideos`: it
            // handles HLS, adaptive bitrate and the signed token in the URL.
            <iframe
              src={video.data.playbackUrl}
              title="Explainer video"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
              className="aspect-video w-full rounded-lg border-0"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
